import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarPlus, ChevronLeft, ChevronRight, Plus, Power } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import SchoolHeader from "../school/components/SchoolHeader";
import AdminSideBar from "./AdminSideBar";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";
const EXAMS = ["NSTSE", "UIEO", "UIMO", "UCO", "UIGKO"];
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const PAGE_SIZE = 5;
const EMPTY_FORM = { examCode: "", date: "", mode: "Offline", isActive: true };

const getDayName = (dateValue) => {
  if (!dateValue) {
    return "";
  }

  const [year, month, day] = dateValue.split("-").map(Number);
  return DAYS[new Date(year, month - 1, day).getDay()] || "";
};

const formatExamDateLabel = (row) => {
  if (row?.label) {
    return row.label;
  }

  if (!row?.date) {
    return "";
  }

  const [year, month, day] = row.date.split("-");
  return `${day}-${month}-${year} (${row.day || getDayName(row.date)})-${row.mode || "Offline"}`;
};

const createEmptyExamColumns = (page = 1) =>
  EXAMS.map((examCode) => ({
    examCode,
    page,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 1,
    dates: [],
  }));

const createEmptyExamColumn = (examCode, page = 1) => ({
  examCode,
  page,
  pageSize: PAGE_SIZE,
  total: 0,
  totalPages: 1,
  dates: [],
});

const ManageExamsDates = () => {
  const navigate = useNavigate();
  const [examColumns, setExamColumns] = useState(createEmptyExamColumns);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const getToken = () => localStorage.getItem("uc_admin_token");

  const loadExamDates = async (targetPage = page) => {
    const token = getToken();
    if (!token) {
      navigate("/admin/login");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/exam-dates`, {
        params: { page: targetPage, pageSize: PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      });

      const loadedColumns = Array.isArray(response.data?.data) ? response.data.data : [];
      setExamColumns(
        EXAMS.map((examCode) => loadedColumns.find((item) => item.examCode === examCode) || createEmptyExamColumn(examCode, targetPage))
      );
      setPage(targetPage);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("uc_admin_token");
        navigate("/admin/login");
      }
      toast.error(error.response?.data?.message || "Unable to load exam dates");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadExamDates(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxTotalPages = Math.max(...examColumns.map((exam) => exam.totalPages || 1), 1);

  const openInsertModal = (examCode) => {
    setFormData({ ...EMPTY_FORM, examCode });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData(EMPTY_FORM);
  };

  const handleInsert = async () => {
    const token = getToken();
    const day = getDayName(formData.date);

    if (!formData.examCode || !formData.date || !day) {
      toast.error("Please select an exam date");
      return;
    }

    setIsSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/admin/exam-dates`,
        {
          examCode: formData.examCode,
          date: formData.date,
          day,
          mode: formData.mode,
          isActive: formData.isActive,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(`${formData.examCode} date inserted`);
      closeModal();
      await loadExamDates(page);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("uc_admin_token");
        navigate("/admin/login");
      }
      toast.error(error.response?.data?.message || "Unable to insert exam date");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async (dateRow) => {
    const token = getToken();

    try {
      await axios.patch(
        `${API_BASE_URL}/admin/exam-dates/${dateRow.id}/toggle-status`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(`Date marked ${dateRow.isActive ? "inactive" : "active"}`);
      await loadExamDates(page);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("uc_admin_token");
        navigate("/admin/login");
      }
      toast.error(error.response?.data?.message || "Unable to update status");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <SchoolHeader />
      <div className="flex flex-col bg-slate-50 lg:flex-row">
      <AdminSideBar />
      <main className="min-w-0 flex-1 px-3 py-5 sm:px-5">
        <section className="mx-auto max-w-7xl space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="space-y-2 text-left">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-600">Admin</p>
              <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Manage Exam Dates</h1>
              <p className="text-sm text-slate-600">Add dates per exam and review active or inactive entries.</p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => loadExamDates(Math.max(1, page - 1))} disabled={isLoading || page <= 1}>
                <ChevronLeft className="mr-2 h-4 w-4" />
                Prev
              </Button>
              <div className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-700">
                Page {page} of {maxTotalPages}
              </div>
              <Button type="button" variant="outline" onClick={() => loadExamDates(Math.min(maxTotalPages, page + 1))} disabled={isLoading || page >= maxTotalPages}>
                Next
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">Loading exam dates...</div>
          ) : (
            <div className="grid gap-3 xl:grid-cols-5">
              {examColumns.map((exam) => (
                <div key={exam.examCode} className="flex min-h-[300px] flex-col rounded-2xl border border-slate-200 bg-slate-50 p-3.5 text-left">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-base font-bold text-slate-900">{exam.examCode}</h2>
                      <p className="text-xs text-slate-500">{exam.total} entries</p>
                    </div>
                    <Button type="button" size="sm" onClick={() => openInsertModal(exam.examCode)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add
                    </Button>
                  </div>

                  <div className="flex flex-1 flex-col gap-3">
                    {exam.dates.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">No dates on this page.</div>
                    ) : (
                      exam.dates.map((dateRow) => (
                        <div key={dateRow.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                          <div className="text-sm font-semibold text-slate-900 ">{formatExamDateLabel(dateRow)}</div>
                          <div className="mt-2.5 flex flex-nowrap items-center gap-1.5">
                            <span className="whitespace-nowrap rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold leading-4 text-slate-700">{dateRow.mode}</span>
                            <span
                              className={`whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-4 ${
                                dateRow.isActive ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                              }`}
                            >
                              {dateRow.isActive ? "Active" : "Inactive"}
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleStatus(dateRow)}
                              className={`h-6 w-6 shrink-0 rounded-full p-0 text-xs font-semibold ${
                                dateRow.isActive
                                  ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:text-rose-800"
                                  : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800"
                              }`}
                              title={dateRow.isActive ? "Mark inactive" : "Mark active"}
                            >
                              <Power className="h-3 w-3" />
                            </Button>
                          </div>
                       
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
      </div>
      <Footer />

      <Dialog open={isModalOpen} onOpenChange={(open) => (open ? setIsModalOpen(true) : closeModal())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add {formData.examCode} Exam Date</DialogTitle>
            <DialogDescription>Select the date and mode. The day is calculated automatically.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-left">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(event) => setFormData((current) => ({ ...current, date: event.target.value }))}
              />
            </div>

            <div>
              <Label>Day</Label>
              <div className="mt-1 flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700">
                {getDayName(formData.date) || "Select date"}
              </div>
            </div>

            <div>
              <Label>Mode</Label>
              <RadioGroup
                value={formData.mode}
                onValueChange={(value) => setFormData((current) => ({ ...current, mode: value }))}
                className="mt-2 flex gap-4"
              >
                {["Offline", "Online"].map((mode) => (
                  <Label key={mode} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <RadioGroupItem value={mode} />
                    {mode}
                  </Label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label>Status</Label>
              <RadioGroup
                value={formData.isActive ? "active" : "inactive"}
                onValueChange={(value) => setFormData((current) => ({ ...current, isActive: value === "active" }))}
                className="mt-2 flex gap-4"
              >
                {[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ].map((status) => (
                  <Label key={status.value} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <RadioGroupItem value={status.value} />
                    {status.label}
                  </Label>
                ))}
              </RadioGroup>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="button" onClick={handleInsert} disabled={isSaving}>
              <CalendarPlus className="mr-2 h-4 w-4" />
              {isSaving ? "Inserting..." : "Insert Date"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageExamsDates;
